import { useEffect, useState, useCallback } from "react";
import { adminApi, shorten } from "@/lib/api";
import { toast } from "sonner";
import { Lock, LogOut, Loader2, LayoutDashboard, Hexagon, Users, Receipt, Settings as SettingsIcon, Trash2 } from "lucide-react";

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("portal_admin_token"));
  return authed ? <Dashboard onLogout={() => { localStorage.removeItem("portal_admin_token"); setAuthed(false); }} />
    : <Login onLogin={() => setAuthed(true)} />;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/login", { email, password });
      localStorage.setItem("portal_admin_token", r.data.access_token);
      toast.success("Mission control authenticated");
      onLogin();
    } catch (err) { toast.error("Invalid credentials"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative z-10">
      <form onSubmit={submit} className="glass border border-cosmic/15 p-10 w-full max-w-md" data-testid="admin-login-form">
        <Lock className="text-portal mb-5" />
        <div className="text-overline mb-2">Restricted Access</div>
        <h1 className="font-display text-2xl uppercase tracking-tight mb-8">Mission Control</h1>
        <label className="text-overline">Operator Email</label>
        <input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
          className="w-full bg-deepspace border border-cosmic/20 px-4 py-3 mt-2 mb-5 outline-none focus:border-portal font-mono text-sm" />
        <label className="text-overline">Passphrase</label>
        <input data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
          className="w-full bg-deepspace border border-cosmic/20 px-4 py-3 mt-2 mb-8 outline-none focus:border-portal font-mono text-sm" />
        <button data-testid="admin-login-btn" disabled={busy} className="btn-portal w-full flex items-center justify-center gap-2">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Authenticate
        </button>
      </form>
    </div>
  );
}

const TABS = [
  { key: "analytics", label: "Analytics", icon: <LayoutDashboard size={15} /> },
  { key: "sectors", label: "Sectors", icon: <Hexagon size={15} /> },
  { key: "explorers", label: "Explorers", icon: <Users size={15} /> },
  { key: "transactions", label: "Transactions", icon: <Receipt size={15} /> },
  { key: "settings", label: "Registry Settings", icon: <SettingsIcon size={15} /> },
];

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [explorers, setExplorers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [settings, setSettings] = useState(null);

  const handle401 = (e) => { if (e.response?.status === 401) { onLogout(); } };

  const load = useCallback(() => {
    adminApi.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(handle401);
    adminApi.get("/admin/sectors").then((r) => setSectors(r.data)).catch(handle401);
    adminApi.get("/admin/explorers").then((r) => setExplorers(r.data)).catch(handle401);
    adminApi.get("/admin/transactions").then((r) => setTxs(r.data)).catch(handle401);
    adminApi.get("/admin/settings").then((r) => setSettings(r.data)).catch(handle401);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const deleteSector = async (id) => {
    if (!window.confirm(`Revoke registration of ${id}?`)) return;
    try { await adminApi.delete(`/admin/sectors/${id}`); toast.success("Registration revoked"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await adminApi.put("/admin/settings", {
        price_usdc: parseFloat(f.get("price_usdc")),
        treasury_address: f.get("treasury_address"),
        erc721_contract_address: f.get("erc721_contract_address"),
        usdc_contract_address: f.get("usdc_contract_address"),
        chain_id: parseInt(f.get("chain_id")),
        public_base_url: f.get("public_base_url"),
        social_x: f.get("social_x"),
        social_telegram: f.get("social_telegram"),
        social_discord: f.get("social_discord"),
        social_instagram: f.get("social_instagram"),
        social_tiktok: f.get("social_tiktok"),
        social_facebook: f.get("social_facebook"),
      });
      toast.success("Registry settings updated");
      load();
    } catch (e) { toast.error("Failed to save"); }
  };

  return (
    <div className="min-h-screen relative z-10 pt-10">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between border-b border-cosmic/10 pb-6">
          <div>
            <div className="text-overline mb-2">Mission Control</div>
            <h1 className="font-display text-2xl md:text-3xl uppercase tracking-tight">Admin Registry</h1>
          </div>
          <button data-testid="admin-logout" onClick={onLogout} className="btn-ghost flex items-center gap-2 py-2.5"><LogOut size={14} /> Exit</button>
        </div>

        <div className="flex flex-wrap gap-1 mt-6 border-b border-cosmic/10">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3 font-mono text-xs tracking-widest uppercase border-b-2 -mb-px ${tab === t.key ? "border-portal text-portal" : "border-transparent text-cosmic/50 hover:text-white"}`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "analytics" && analytics && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-cosmic/10 border border-cosmic/10">
              <Metric label="Total Sectors" value={analytics.total_sectors.toLocaleString()} />
              <Metric label="Registered" value={analytics.claimed_sectors.toLocaleString()} />
              <Metric label="Available" value={analytics.available_sectors.toLocaleString()} />
              <Metric label="Explorers" value={analytics.total_explorers.toLocaleString()} />
              <Metric label="Listed (Resale)" value={analytics.listed_sectors} />
              <Metric label="Transactions" value={analytics.total_transactions} />
              <Metric label="Registration Revenue" value={`${analytics.registration_revenue_usdc} USDC`} />
            </div>
          )}

          {tab === "sectors" && (
            <Table head={["Sector", "Name", "Owner", "Token", "Tx", ""]}>
              {sectors.map((s) => (
                <tr key={s.sector_id} className="border-b border-cosmic/10 hover:bg-portal/5">
                  <Td className="text-portal">{s.sector_id}</Td>
                  <Td>{s.name}</Td>
                  <Td>{shorten(s.owner_wallet)}</Td>
                  <Td>#{s.nft_token_id}</Td>
                  <Td>{shorten(s.tx_hash)}</Td>
                  <Td><button onClick={() => deleteSector(s.sector_id)} className="text-mars hover:text-red-400"><Trash2 size={14} /></button></Td>
                </tr>
              ))}
            </Table>
          )}

          {tab === "explorers" && (
            <Table head={["Explorer", "Wallet", "Badge", "Joined"]}>
              {explorers.map((e) => (
                <tr key={e.wallet_address} className="border-b border-cosmic/10 hover:bg-portal/5">
                  <Td>{e.explorer_name}</Td><Td>{shorten(e.wallet_address)}</Td>
                  <Td>{e.badge}</Td><Td>{new Date(e.join_date).toLocaleDateString()}</Td>
                </tr>
              ))}
            </Table>
          )}

          {tab === "transactions" && (
            <Table head={["Type", "Sector", "From", "To", "Amount", "Date"]}>
              {txs.map((t) => (
                <tr key={t.id} className="border-b border-cosmic/10 hover:bg-portal/5">
                  <Td className="text-portal uppercase">{t.type}</Td><Td>{t.sector_id}</Td>
                  <Td>{shorten(t.from_wallet)}</Td><Td>{shorten(t.to_wallet)}</Td>
                  <Td>{t.amount} {t.currency}</Td><Td>{new Date(t.timestamp).toLocaleDateString()}</Td>
                </tr>
              ))}
            </Table>
          )}

          {tab === "settings" && settings && (
            <form onSubmit={saveSettings} className="glass border border-cosmic/10 p-8 max-w-2xl space-y-5" data-testid="admin-settings-form">
              <SetField name="price_usdc" label="Sector Price (USDC)" def={settings.price_usdc} type="number" />
              <SetField name="chain_id" label="Chain ID" def={settings.chain_id} type="number" />
              <SetField name="usdc_contract_address" label="USDC Contract Address" def={settings.usdc_contract_address} />
              <SetField name="treasury_address" label="Treasury Wallet (enables live USDC settlement)" def={settings.treasury_address} placeholder="0x..." />
              <SetField name="erc721_contract_address" label="ERC-721 Contract Address (OpenSea)" def={settings.erc721_contract_address} placeholder="0x..." />
              <SetField name="public_base_url" label="Public Base URL (for NFT metadata & images)" def={settings.public_base_url} placeholder="https://your-domain" />

              <div className="pt-4 border-t border-cosmic/10">
                <div className="text-overline mb-1">Social Links</div>
                <p className="text-xs text-cosmic/40 font-mono mb-4">Paste full URLs once accounts are created. Empty fields are hidden in the footer.</p>
              </div>
              <SetField name="social_x" label="X (Twitter) URL" def={settings.social_x} placeholder="https://x.com/..." />
              <SetField name="social_telegram" label="Telegram URL" def={settings.social_telegram} placeholder="https://t.me/..." />
              <SetField name="social_discord" label="Discord URL" def={settings.social_discord} placeholder="https://discord.gg/..." />
              <SetField name="social_instagram" label="Instagram URL" def={settings.social_instagram} placeholder="https://instagram.com/..." />
              <SetField name="social_tiktok" label="TikTok URL" def={settings.social_tiktok} placeholder="https://tiktok.com/@..." />
              <SetField name="social_facebook" label="Facebook URL" def={settings.social_facebook} placeholder="https://facebook.com/..." />
              <button data-testid="admin-settings-save" className="btn-portal">Save Registry Settings</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-deepspace/40 p-6">
      <div className="font-display text-2xl md:text-3xl text-white">{value}</div>
      <div className="text-overline mt-2">{label}</div>
    </div>
  );
}
function Table({ head, children }) {
  return (
    <div className="border border-cosmic/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-overline">{head.map((h, i) => <th key={i} className="text-left px-4 py-3 font-normal">{h}</th>)}</tr></thead>
        <tbody className="font-mono text-cosmic/80">{children}</tbody>
      </table>
    </div>
  );
}
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
function SetField({ name, label, def, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-overline">{label}</label>
      <input name={name} defaultValue={def} type={type} placeholder={placeholder}
        className="w-full bg-deepspace border border-cosmic/20 px-4 py-3 mt-2 outline-none focus:border-portal font-mono text-sm" />
    </div>
  );
}
